import { useState } from 'react';
import { useBell } from '@magicbell/react-headless';
import { Divider, IconButton, Stack, Tabs, Tab, Tooltip } from '@mui/material';
import Label from 'src/components/label';
import HeadlessActivityFeed from './HeadlessActvityFeed';
import HeadlessNotificationList from './HeadlessNotificationList';
import { MarkEmailRead } from '@mui/icons-material';
import { ReportsProjectFilter } from 'src/@types/project';
import { Range } from 'src/components/date-range-picker';
import ReportsFilters from 'src/sections/main/reports/ReportsFilters';
import { Teammate } from 'src/@types/teammates';
import { ReportsGeographyFilter } from 'src/@types/geographyFilters';
import { ReportsTeamFilter } from 'src/@types/team';
import { TOutreachType } from 'src/@types/outreachType';
import ActivityFeedExport from 'src/sections/main/reports/ActivityFeedExport';
import { roleHasPageAccess } from 'src/auth/rbac';
import { useUserRole } from 'src/hooks/useUserRole';

const SPACING = 2.5;

function HeadlessNotificationsTabs({ isOpen = false }: { isOpen: boolean }) {
  const [filteredOutreachType, setFilteredOutreachType] = useState<TOutreachType | null>(null);
  const [filteredUser, setFilteredUser] = useState<Teammate | Partial<Teammate> | null>(null);
  const [filteredTeam, setFilteredTeam] = useState<ReportsTeamFilter | null>(null);
  const [filteredProject, setFilteredProject] = useState<ReportsProjectFilter | null>(null);
  const [filteredDateRange, setFilteredDateRange] = useState<Range | null>(null);
  const [filteredCounty, setFilteredCounty] = useState<ReportsGeographyFilter | null>(null);
  const [filteredPrecinct, setFilteredPrecinct] = useState<ReportsGeographyFilter | null>(null);
  const [filteredCongressionalDistrict, setFilteredCongressionalDistrict] =
    useState<ReportsGeographyFilter | null>(null);
  const [filteredStateHouseDistrict, setFilteredStateHouseDistrict] =
    useState<ReportsGeographyFilter | null>(null);
  const [filteredStateSenateDistrict, setFilteredStateSenateDistrict] =
    useState<ReportsGeographyFilter | null>(null);
  const [filteredDma, setFilteredDma] = useState<ReportsGeographyFilter | null>(null);
  const [shouldGroupCanvassesByHousehold, setShouldGroupCanvassesByHousehold] = useState(true);

  const { data: userRole } = useUserRole();

  const [currentInbox, setCurrentInbox] = useState('messages');

  const hasReportsAccess = roleHasPageAccess('/reports', userRole);

  const notificationStore = useBell({ storeId: 'inbox' });

  return (
    <>
      <Stack
        direction="row"
        gap={1}
        paddingLeft={SPACING}
        paddingRight={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Tabs
          sx={{ flexGrow: 1 }}
          value={currentInbox}
          onChange={(_event, value) => {
            setCurrentInbox(value);
          }}
        >
          <Tab
            label="Messages"
            value="messages"
            iconPosition="end"
            icon={<Label color="primary">{notificationStore?.unreadCount}</Label>}
          />
          {hasReportsAccess && <Tab label="Activity" value="activity" />}
        </Tabs>

        {isOpen && currentInbox == 'messages' && (
          <Tooltip title="Mark all as read" arrow>
            <IconButton onClick={() => notificationStore?.markAllAsRead()}>
              <MarkEmailRead />
            </IconButton>
          </Tooltip>
        )}

        {isOpen && currentInbox == 'activity' && (
          <Stack direction="row" gap={0.25} paddingY={0.5} justifyContent="end">
            <ReportsFilters
              setFilteredUser={setFilteredUser}
              filteredUser={filteredUser}
              setFilteredProject={setFilteredProject}
              filteredProject={filteredProject}
              setFilteredDateRange={setFilteredDateRange}
              filteredDateRange={filteredDateRange}
              setFilteredCounty={setFilteredCounty}
              filteredCounty={filteredCounty}
              setFilteredPrecinct={setFilteredPrecinct}
              filteredPrecinct={filteredPrecinct}
              setFilteredCongressionalDistrict={setFilteredCongressionalDistrict}
              filteredCongressionalDistrict={filteredCongressionalDistrict}
              setFilteredStateHouseDistrict={setFilteredStateHouseDistrict}
              filteredStateHouseDistrict={filteredStateHouseDistrict}
              setFilteredStateSenateDistrict={setFilteredStateSenateDistrict}
              filteredStateSenateDistrict={filteredStateSenateDistrict}
              setFilteredDma={setFilteredDma}
              filteredDma={filteredDma}
              setFilteredTeam={setFilteredTeam}
              filteredTeam={filteredTeam}
              filteredOutreachType={filteredOutreachType}
              setFilteredOutreachType={setFilteredOutreachType}
              showChips={false}
              shouldGroupCanvassesByHousehold={shouldGroupCanvassesByHousehold}
              setShouldGroupCanvassesByHousehold={setShouldGroupCanvassesByHousehold}
            />
            <ActivityFeedExport
              filteredOutreachType={filteredOutreachType}
              filteredProject={filteredProject}
              filteredUser={filteredUser}
              filteredDateRange={filteredDateRange}
              filteredCounty={filteredCounty}
              filteredPrecinct={filteredPrecinct}
              filteredCongressionalDistrict={filteredCongressionalDistrict}
              filteredStateHouseDistrict={filteredStateHouseDistrict}
              filteredStateSenateDistrict={filteredStateSenateDistrict}
              filteredDma={filteredDma}
              filteredTeam={filteredTeam}
            />
          </Stack>
        )}
      </Stack>

      <Divider />

      {isOpen && currentInbox == 'messages' && <HeadlessNotificationList />}

      {isOpen && currentInbox == 'activity' && (
        <HeadlessActivityFeed
          setFilteredUser={setFilteredUser}
          filteredUser={filteredUser}
          setFilteredProject={setFilteredProject}
          filteredProject={filteredProject}
          setFilteredDateRange={setFilteredDateRange}
          filteredDateRange={filteredDateRange}
          setFilteredCounty={setFilteredCounty}
          filteredCounty={filteredCounty}
          setFilteredPrecinct={setFilteredPrecinct}
          filteredPrecinct={filteredPrecinct}
          setFilteredCongressionalDistrict={setFilteredCongressionalDistrict}
          filteredCongressionalDistrict={filteredCongressionalDistrict}
          setFilteredStateHouseDistrict={setFilteredStateHouseDistrict}
          filteredStateHouseDistrict={filteredStateHouseDistrict}
          setFilteredStateSenateDistrict={setFilteredStateSenateDistrict}
          filteredStateSenateDistrict={filteredStateSenateDistrict}
          setFilteredDma={setFilteredDma}
          filteredDma={filteredDma}
          setFilteredTeam={setFilteredTeam}
          filteredTeam={filteredTeam}
          filteredOutreachType={filteredOutreachType}
          setFilteredOutreachType={setFilteredOutreachType}
        />
      )}
    </>
  );
}

export default HeadlessNotificationsTabs;
